import {
  CheckCircleIcon, ClockIcon, XCircleIcon, PencilIcon,
  EyeIcon, ScaleIcon, BriefcaseIcon, ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

export type ApprovalStatusValue =
  | 'DRAFT'
  | 'PENDING_REVIEW'
  | 'UNDER_REVIEW'
  | 'SECOND_REVIEW'
  | 'LEGAL_REVIEW'
  | 'RETURNED'
  | 'APPROVED'
  | 'REJECTED';

interface ApprovalStatusProps {
  status: ApprovalStatusValue;
  className?: string;
}

export default function ApprovalStatus({ status, className }: ApprovalStatusProps) {
  const getStatusConfig = () => {
    switch (status) {
      case 'DRAFT':
        return {
          label: 'Draft',
          icon: PencilIcon,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-200',
        };
      case 'PENDING_REVIEW':
        return {
          label: 'Pending Review',
          icon: ClockIcon,
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          borderColor: 'border-yellow-200',
        };
      case 'UNDER_REVIEW':
        return {
          label: 'Under Review',
          icon: EyeIcon,
          bgColor: 'bg-teal-100',
          textColor: 'text-teal-800',
          borderColor: 'border-teal-200',
        };
      case 'SECOND_REVIEW':
        return {
          label: 'Second Review',
          icon: ScaleIcon,
          bgColor: 'bg-purple-100',
          textColor: 'text-purple-800',
          borderColor: 'border-purple-200',
        };
      case 'LEGAL_REVIEW':
        return {
          label: 'Legal Review',
          icon: BriefcaseIcon,
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          borderColor: 'border-blue-200',
        };
      case 'RETURNED':
        return {
          label: 'Returned',
          icon: ArrowUturnLeftIcon,
          bgColor: 'bg-amber-100',
          textColor: 'text-amber-800',
          borderColor: 'border-amber-200',
        };
      case 'APPROVED':
        return {
          label: 'Approved',
          icon: CheckCircleIcon,
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          borderColor: 'border-green-200',
        };
      case 'REJECTED':
        return {
          label: 'Rejected',
          icon: XCircleIcon,
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          borderColor: 'border-red-200',
        };
      default:
        return {
          label: 'Unknown',
          icon: ClockIcon,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-200',
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <span
      className={clsx(
        'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border',
        config.bgColor,
        config.textColor,
        config.borderColor,
        className
      )}
    >
      <Icon className="w-4 h-4 mr-1.5" />
      {config.label}
    </span>
  );
}
